import React, { useEffect, useMemo, useState } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { instructorApi, userDataTransferApi } from '../../lib/api';
import { toast } from 'sonner';
import {
    Loader2, ArrowRightLeft, Search, User, AlertTriangle, ArrowRight, CheckCircle2,
} from 'lucide-react';

/**
 * UserDataTransferDialog
 * ----------------------
 * Super-Admin-Tool: Überträgt selektiv Datensätze (Bescheinigungen, Lizenzen,
 * Buchungen, Rechnungen etc.) von einem Quell-Benutzer auf einen Ziel-Benutzer.
 *
 * Anwendungsfall (Production-Use-Case 05/2026):
 *   Ein Einrichtungsleiter hat Mitarbeiter unter seiner eigenen E-Mail
 *   registriert und für diese Seminare gebucht. Die Mitarbeiter haben nun
 *   eigene Accounts — die alten Bescheinigungen müssen auf die echten
 *   Accounts migriert werden.
 *
 * Quell-Account bleibt erhalten. Löschung erfolgt anschließend separat
 * (DSGVO-Pfad oder Super-Admin-Action).
 */

const CATEGORY_LABELS = {
    training_records: { label: 'Lehrgangs­bescheinigungen', desc: 'Teilnahme­bescheinigungen, UE-Nachweise, Review-Anträge' },
    licenses: { label: 'Lizenzen', desc: 'Ausgestellte Lizenzen / Zertifikate' },
    license_renewals: { label: 'Lizenz-Verlängerungs­anträge', desc: 'Anträge auf Verlängerung / Fremdlizenz-Umwandlung' },
    bookings: { label: 'Buchungen', desc: 'Seminar-Buchungen und Anmeldungen' },
    cancellations: { label: 'Stornierungen', desc: 'Stornierungs­datensätze' },
    invoices: { label: 'Rechnungen / Belege', desc: 'Ausgestellte Rechnungen und Quittungen' },
    qr_scans: { label: 'QR-Anwesenheits-Scans', desc: 'Eingecheckte QR-Codes für Seminare' },
};

export function UserDataTransferDialog({ open, onOpenChange, defaultTargetId = null, onDone }) {
    // --- User-Liste laden ---
    const [members, setMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);

    // --- Auswahl Quelle / Ziel ---
    const [sourceQuery, setSourceQuery] = useState('');
    const [targetQuery, setTargetQuery] = useState('');
    const [sourceId, setSourceId] = useState('');
    const [targetId, setTargetId] = useState(defaultTargetId || '');

    // --- Summary der Quell-Daten ---
    const [sourceSummary, setSourceSummary] = useState(null);
    const [loadingSummary, setLoadingSummary] = useState(false);

    // --- Checkboxen für Kategorien ---
    const [flags, setFlags] = useState({
        training_records: true,
        licenses: true,
        license_renewals: true,
        bookings: true,
        cancellations: true,
        invoices: true,
        qr_scans: true,
    });

    const [note, setNote] = useState('');
    const [moveDropbox, setMoveDropbox] = useState(true);
    const [executing, setExecuting] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [result, setResult] = useState(null);

    // Reset state when dialog opens/closes
    useEffect(() => {
        if (open) {
            setSourceQuery('');
            setTargetQuery('');
            setSourceId('');
            setTargetId(defaultTargetId || '');
            setSourceSummary(null);
            setNote('');
            setConfirmed(false);
            setResult(null);
            setLoadingMembers(true);
            instructorApi.getAll(false)
                .then(r => setMembers(r.data || []))
                .catch(() => setMembers([]))
                .finally(() => setLoadingMembers(false));
        }
    }, [open, defaultTargetId]);

    // Summary für Quelle laden, sobald ausgewählt
    useEffect(() => {
        if (!sourceId) {
            setSourceSummary(null);
            return;
        }
        const member = members.find(m => m.user_id === sourceId || m.id === sourceId);
        const userId = member?.user_id || sourceId;
        if (!userId) return;
        setLoadingSummary(true);
        userDataTransferApi.getSummary(userId)
            .then(r => setSourceSummary(r.data))
            .catch(err => {
                toast.error(err.response?.data?.detail || 'Daten-Übersicht konnte nicht geladen werden');
                setSourceSummary(null);
            })
            .finally(() => setLoadingSummary(false));
    }, [sourceId, members]);

    const filteredForSource = useMemo(() => {
        const q = sourceQuery.trim().toLowerCase();
        if (!q) return members.slice(0, 25);
        return members.filter(m => {
            const hay = `${m.first_name || ''} ${m.last_name || ''} ${m.email || ''}`.toLowerCase();
            return hay.includes(q);
        }).slice(0, 25);
    }, [members, sourceQuery]);

    const filteredForTarget = useMemo(() => {
        const q = targetQuery.trim().toLowerCase();
        if (!q) return members.slice(0, 25);
        return members.filter(m => {
            const hay = `${m.first_name || ''} ${m.last_name || ''} ${m.email || ''}`.toLowerCase();
            return hay.includes(q);
        }).slice(0, 25);
    }, [members, targetQuery]);

    const sourceMember = members.find(m => (m.user_id || m.id) === sourceId);
    const targetMember = members.find(m => (m.user_id || m.id) === targetId);

    const totalSelected = useMemo(() => {
        if (!sourceSummary) return 0;
        return Object.entries(flags).reduce((acc, [k, on]) => {
            if (on && sourceSummary.counts?.[k]) return acc + sourceSummary.counts[k];
            return acc;
        }, 0);
    }, [flags, sourceSummary]);

    const handleExecute = async () => {
        if (!sourceId || !targetId) {
            toast.error('Bitte Quelle und Ziel auswählen');
            return;
        }
        if (sourceId === targetId) {
            toast.error('Quelle und Ziel müssen unterschiedlich sein');
            return;
        }
        const selected = Object.entries(flags).filter(([, v]) => v).map(([k]) => k);
        if (selected.length === 0) {
            toast.error('Mindestens eine Datenkategorie auswählen');
            return;
        }
        setExecuting(true);
        try {
            // Wichtig: API erwartet die user_id (nicht instructor_id)
            const sourceUserId = sourceMember?.user_id || sourceId;
            const targetUserId = targetMember?.user_id || targetId;
            const r = await userDataTransferApi.transfer({
                source_user_id: sourceUserId,
                target_user_id: targetUserId,
                transfer: flags,
                note: note.trim() || null,
                move_dropbox: moveDropbox,
            });
            setResult(r.data);
            toast.success(r.data?.message || 'Übertragung abgeschlossen');
            if (onDone) onDone(r.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Übertragung fehlgeschlagen');
        } finally {
            setExecuting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="user-data-transfer-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-heading">
                        <ArrowRightLeft className="h-5 w-5 text-primary" />
                        Datensätze zwischen Mitgliedern übertragen
                    </DialogTitle>
                    <DialogDescription>
                        Übertragen Sie Bescheinigungen, Lizenzen, Buchungen usw. von einem
                        Quell-Konto auf ein Ziel-Konto. Nützlich, wenn ein Einrichtungsleiter
                        Mitarbeiter unter eigener E-Mail registriert hatte. Der Quell-Account
                        bleibt erhalten — Löschung erfolgt separat.
                    </DialogDescription>
                </DialogHeader>

                {result ? (
                    // -------- ERFOLGS-VIEW NACH ÜBERTRAGUNG --------
                    <div className="space-y-4" data-testid="transfer-result-view">
                        <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-start gap-3">
                            <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                            <div>
                                <div className="font-semibold text-green-900">Übertragung erfolgreich</div>
                                <div className="text-sm text-green-800 mt-1">{result.message}</div>
                            </div>
                        </div>
                        <div className="border rounded-md divide-y">
                            {Object.entries(result.transferred || {}).map(([cat, count]) => (
                                <div key={cat} className="flex justify-between items-center px-4 py-2.5 text-sm" data-testid={`result-cat-${cat}`}>
                                    <span>{CATEGORY_LABELS[cat]?.label || cat}</span>
                                    <span className="font-mono font-semibold text-gray-900">{count}</span>
                                </div>
                            ))}
                        </div>
                        {result.dropbox && (
                            <div className={`text-sm rounded-md p-3 border ${result.dropbox.moved ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-amber-50 border-amber-200 text-amber-900'}`} data-testid="result-dropbox">
                                <strong>Dropbox:</strong>{' '}
                                {result.dropbox.moved
                                    ? `Ordner übertragen (${result.dropbox.files_moved ?? 0} Einträge) · ${result.dropbox.reason || ''}`
                                    : `Nicht übertragen — ${result.dropbox.reason || 'unbekannter Grund'}`}
                            </div>
                        )}
                        <div className="text-xs text-muted-foreground">
                            Hinweis: Der Quell-Account <strong>{result.source?.email}</strong> ist
                            jetzt leer. Bevor Sie ihn löschen, prüfen Sie noch einmal die Daten
                            des Ziel-Accounts <strong>{result.target?.email}</strong>.
                        </div>
                        <DialogFooter>
                            <Button onClick={() => onOpenChange(false)} data-testid="transfer-close-btn">Schließen</Button>
                        </DialogFooter>
                    </div>
                ) : (
                    <div className="space-y-5">
                        {/* SCHRITT 1: Quelle + Ziel auswählen */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* QUELLE */}
                            <div className="space-y-2 border rounded-md p-3 bg-rose-50/30 border-rose-200">
                                <Label className="font-semibold text-rose-900">1. Quell-Konto (Daten kommen FROM)</Label>
                                <div className="relative">
                                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        value={sourceQuery}
                                        onChange={(e) => setSourceQuery(e.target.value)}
                                        placeholder="Suche nach Name oder E-Mail…"
                                        className="pl-8 text-sm"
                                        data-testid="source-search-input"
                                    />
                                </div>
                                <div className="max-h-48 overflow-y-auto border rounded bg-white">
                                    {loadingMembers ? (
                                        <div className="p-3 text-sm text-muted-foreground flex items-center gap-2">
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Lade Mitglieder…
                                        </div>
                                    ) : filteredForSource.length === 0 ? (
                                        <div className="p-3 text-sm text-muted-foreground">Keine Treffer</div>
                                    ) : (
                                        filteredForSource.map(m => {
                                            const uid = m.user_id || m.id;
                                            const selected = sourceId === uid;
                                            return (
                                                <button
                                                    key={uid}
                                                    type="button"
                                                    onClick={() => setSourceId(uid)}
                                                    className={`block w-full text-left px-3 py-1.5 text-sm hover:bg-rose-100 ${selected ? 'bg-rose-200 font-semibold' : ''}`}
                                                    data-testid={`source-pick-${uid}`}
                                                >
                                                    {m.first_name} {m.last_name}<br />
                                                    <span className="text-xs text-muted-foreground">{m.email}</span>
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                                {sourceMember && (
                                    <div className="text-xs bg-white rounded border p-2 mt-1">
                                        <strong>Ausgewählt:</strong> {sourceMember.first_name} {sourceMember.last_name}
                                        <div className="text-muted-foreground">{sourceMember.email}</div>
                                    </div>
                                )}
                            </div>

                            {/* ZIEL */}
                            <div className="space-y-2 border rounded-md p-3 bg-emerald-50/30 border-emerald-200">
                                <Label className="font-semibold text-emerald-900">2. Ziel-Konto (Daten gehen TO)</Label>
                                <div className="relative">
                                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        value={targetQuery}
                                        onChange={(e) => setTargetQuery(e.target.value)}
                                        placeholder="Suche nach Name oder E-Mail…"
                                        className="pl-8 text-sm"
                                        data-testid="target-search-input"
                                    />
                                </div>
                                <div className="max-h-48 overflow-y-auto border rounded bg-white">
                                    {loadingMembers ? (
                                        <div className="p-3 text-sm text-muted-foreground flex items-center gap-2">
                                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Lade Mitglieder…
                                        </div>
                                    ) : filteredForTarget.length === 0 ? (
                                        <div className="p-3 text-sm text-muted-foreground">Keine Treffer</div>
                                    ) : (
                                        filteredForTarget.map(m => {
                                            const uid = m.user_id || m.id;
                                            const selected = targetId === uid;
                                            const isSource = sourceId === uid;
                                            return (
                                                <button
                                                    key={uid}
                                                    type="button"
                                                    onClick={() => !isSource && setTargetId(uid)}
                                                    disabled={isSource}
                                                    className={`block w-full text-left px-3 py-1.5 text-sm ${isSource ? 'opacity-40 cursor-not-allowed' : 'hover:bg-emerald-100'} ${selected ? 'bg-emerald-200 font-semibold' : ''}`}
                                                    data-testid={`target-pick-${uid}`}
                                                >
                                                    {m.first_name} {m.last_name}
                                                    {isSource && <span className="text-rose-600 ml-1">(Quelle)</span>}
                                                    <br />
                                                    <span className="text-xs text-muted-foreground">{m.email}</span>
                                                </button>
                                            );
                                        })
                                    )}
                                </div>
                                {targetMember && (
                                    <div className="text-xs bg-white rounded border p-2 mt-1">
                                        <strong>Ausgewählt:</strong> {targetMember.first_name} {targetMember.last_name}
                                        <div className="text-muted-foreground">{targetMember.email}</div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* SCHRITT 2: Datensätze in Quelle anzeigen + Auswahl */}
                        {sourceId && (
                            <div className="border rounded-md p-4 bg-gray-50">
                                <div className="flex items-center justify-between mb-3">
                                    <Label className="font-semibold">3. Welche Datensätze übertragen?</Label>
                                    {sourceSummary && (
                                        <div className="text-xs text-muted-foreground">
                                            Insgesamt in Quelle: <strong>{sourceSummary.total}</strong> Datensätze
                                        </div>
                                    )}
                                </div>

                                {loadingSummary ? (
                                    <div className="text-sm text-muted-foreground flex items-center gap-2">
                                        <Loader2 className="h-4 w-4 animate-spin" /> Lade Daten-Übersicht…
                                    </div>
                                ) : sourceSummary ? (
                                    <div className="space-y-2">
                                        {Object.entries(CATEGORY_LABELS).map(([key, info]) => {
                                            const count = sourceSummary.counts?.[key] || 0;
                                            const disabled = count === 0;
                                            return (
                                                <label
                                                    key={key}
                                                    className={`flex items-start gap-3 p-2 rounded border bg-white ${disabled ? 'opacity-50' : 'cursor-pointer hover:border-primary'}`}
                                                    data-testid={`transfer-cat-${key}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={flags[key]}
                                                        disabled={disabled}
                                                        onChange={(e) => setFlags({ ...flags, [key]: e.target.checked })}
                                                        className="mt-1"
                                                    />
                                                    <div className="flex-1">
                                                        <div className="flex items-center justify-between">
                                                            <span className="font-medium text-sm">{info.label}</span>
                                                            <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">{count}</span>
                                                        </div>
                                                        <div className="text-xs text-muted-foreground">{info.desc}</div>
                                                    </div>
                                                </label>
                                            );
                                        })}
                                    </div>
                                ) : null}
                            </div>
                        )}

                        {/* SCHRITT 3: Notiz + Bestätigung */}
                        {sourceId && targetId && sourceSummary && (
                            <div className="space-y-3">
                                <div>
                                    <Label htmlFor="transfer-note" className="text-sm">Notiz (optional, für Audit-Log)</Label>
                                    <Input
                                        id="transfer-note"
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                        placeholder="z. B. „Konto ursprünglich vom Einrichtungsleiter angelegt"
                                        className="mt-1"
                                        data-testid="transfer-note-input"
                                    />
                                </div>

                                <label className="flex items-start gap-3 p-3 rounded border bg-white cursor-pointer hover:border-primary" data-testid="move-dropbox-toggle">
                                    <input
                                        type="checkbox"
                                        checked={moveDropbox}
                                        onChange={(e) => setMoveDropbox(e.target.checked)}
                                        className="mt-1"
                                    />
                                    <div className="text-sm">
                                        <div className="font-medium">Dropbox-Ordner ebenfalls übertragen <span className="text-xs text-emerald-600 font-normal">(empfohlen)</span></div>
                                        <div className="text-xs text-muted-foreground mt-0.5">
                                            Verschiebt PDFs (Bescheinigungen, Rechnungen, Lizenzen) aus dem
                                            Quell-Ordner in den Ziel-Ordner. Ohne diese Option bleiben die
                                            Dateien im Quell-Ordner und werden bei einer späteren Löschung
                                            des Quell-Accounts mit-gelöscht.
                                        </div>
                                    </div>
                                </label>

                                <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-r text-sm">
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                        <div className="text-amber-900">
                                            <strong>Bitte sorgfältig prüfen:</strong> Diese Aktion ist NICHT
                                            rückgängig zu machen. Es werden <strong>{totalSelected}</strong> Datensätze von
                                            <span className="inline-flex items-center gap-1 mx-1">
                                                <User className="h-3.5 w-3.5" />
                                                <strong>{sourceMember?.email}</strong>
                                            </span>
                                            <ArrowRight className="inline h-3.5 w-3.5 mx-1" />
                                            <span className="inline-flex items-center gap-1">
                                                <User className="h-3.5 w-3.5" />
                                                <strong>{targetMember?.email}</strong>
                                            </span> übertragen.
                                        </div>
                                    </div>
                                </div>

                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={confirmed}
                                        onChange={(e) => setConfirmed(e.target.checked)}
                                        data-testid="transfer-confirm-checkbox"
                                    />
                                    Ich bestätige, dass die Auswahl korrekt ist.
                                </label>
                            </div>
                        )}

                        <DialogFooter>
                            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={executing}>
                                Abbrechen
                            </Button>
                            <Button
                                onClick={handleExecute}
                                disabled={!sourceId || !targetId || !sourceSummary || !confirmed || executing || totalSelected === 0}
                                data-testid="transfer-execute-btn"
                            >
                                {executing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {totalSelected > 0 ? `${totalSelected} Datensätze übertragen` : 'Datensätze übertragen'}
                            </Button>
                        </DialogFooter>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
